import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Chip, Avatar, Divider, Button
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import PersonIcon from '@mui/icons-material/Person';
import DevicesIcon from '@mui/icons-material/Devices';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import StoreIcon from '@mui/icons-material/Store';
import LoginIcon from '@mui/icons-material/Login';
import SearchIcon from '@mui/icons-material/Search';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import BuildIcon from '@mui/icons-material/Build';
import VerifiedIcon from '@mui/icons-material/Verified';
import SmsIcon from '@mui/icons-material/Sms';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

const STEP_META = [
  { n: 1, name: 'Check-in', hint: 'Job received', Icon: LoginIcon },
  { n: 2, name: 'Diagnosis', hint: 'Find the fault', Icon: SearchIcon },
  { n: 3, name: 'Estimate', hint: 'Quote & approve', Icon: RequestQuoteIcon },
  { n: 4, name: 'Repair', hint: 'Fix the item', Icon: BuildIcon },
  { n: 5, name: 'Testing', hint: 'Final check / QA', Icon: VerifiedIcon },
  { n: 6, name: 'Notify', hint: 'Tell the customer', Icon: SmsIcon },
  { n: 7, name: 'Handover', hint: 'Pay & collect', Icon: LocalShippingIcon }
];

const fmtWhen = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const stepState = (workflow = [], stepNumber) => {
  const row = workflow.find((s) => s.step_number === stepNumber);
  const completed = Boolean(row?.is_completed);
  const firstOpen = workflow.length
    ? ((workflow.find((s) => !s.is_completed) || {}).step_number
      || (workflow.every((s) => s.is_completed) ? 7 : 1))
    : 1;
  return {
    completed,
    current: !completed && firstOpen === stepNumber,
    at: row?.completed_at,
    notes: row?.notes,
    name: row?.step_name
  };
};

const stepDoneLabel = (data, n) => {
  switch (n) {
    case 1:
      return data.received_date
        ? `Received ${new Date(data.received_date).toLocaleDateString('en-GB')}`
        : 'Job created';
    case 2: {
      const st = (data.diagnosis_approval_status || '').replace(/_/g, ' ');
      if (st === 'approved') return 'Diagnosis approved';
      if (st === 'pending approval') return 'Waiting approval';
      return data.diagnosis ? 'Diagnosis saved' : 'Not started';
    }
    case 3:
      if (data.status === 'approved') return 'Customer approved';
      if (data.status === 'quoted') return 'Quotation sent';
      if (data.service_coverage === 'warranty' && !data.warranty_extras_billable) return 'Warranty — no quote';
      return 'Waiting quotation';
    case 4:
      if (data.ready_for_collection) return 'Repair finished';
      if (data.parts_received) return 'Parts received — repairing';
      return 'In workshop';
    case 5:
      if (data.qa_status === 'passed' || data.qa_status === 'pass') return 'QA passed';
      if (data.qa_status === 'failed' || data.qa_status === 'fail') return 'QA failed';
      if (data.ready_for_collection) return 'Ready to collect';
      return 'Not tested yet';
    case 6: {
      const sms = (data.smsLogs || []).length;
      return sms ? `${sms} SMS sent` : 'No notification yet';
    }
    case 7:
      if (data.collected_at) return `Collected ${fmtWhen(data.collected_at)}`;
      if (data.invoice_number) return `Invoice ${data.invoice_number}`;
      return 'Waiting collection';
    default:
      return '';
  }
};

export const currentWorkflowStep = (workflow = []) => {
  if (!workflow.length) return 1;
  const open = workflow.find((s) => !s.is_completed);
  return open?.step_number || 7;
};

const RepairJobDashboard = ({
  data,
  expandedStep,
  onSelectStep,
  children
}) => {
  const navigate = useNavigate();
  const workflow = data?.workflow || [];
  const timeline = [...(data.timeline || [])].reverse();
  const doneCount = workflow.filter((s) => s.is_completed).length;
  const openCustomer = data?.customer_id
    ? () => navigate(`/repairs/customers/${data.customer_id}`)
    : null;

  return (
    <Box>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { icon: <PersonIcon fontSize="small" />, label: 'Customer', value: data.customer_name, sub: [data.customer_phone, data.customer_email].filter(Boolean).join(' · '), onClick: openCustomer },
          { icon: <DevicesIcon fontSize="small" />, label: 'Device', value: [data.device_brand, data.device_model].filter(Boolean).join(' ') || data.device_type || '-', sub: [data.device_type, data.device_serial || data.device_imei].filter(Boolean).join(' · ') },
          { icon: <ReportProblemIcon fontSize="small" />, label: 'Issue', value: data.device_issue || '-', sub: data.priority ? `Priority: ${data.priority}` : '' },
          { icon: <StoreIcon fontSize="small" />, label: 'Branch', value: data.branch?.name || '-', sub: data.grn_number ? `JOB ${data.grn_number}` : '' }
        ].map((c) => (
          <Grid item xs={12} sm={6} md={3} key={c.label}>
            <Paper
              variant="outlined"
              onClick={c.onClick}
              sx={{ p: 1.5, height: '100%', cursor: c.onClick ? 'pointer' : 'default', '&:hover': c.onClick ? { borderColor: 'primary.main' } : undefined }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, color: 'text.secondary' }}>
                {c.icon}
                <Typography variant="caption" fontWeight={700} letterSpacing={0.4}>
                  {c.label.toUpperCase()}
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600} noWrap title={c.value}>
                {c.value}
              </Typography>
              {c.sub && (
                <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {c.sub}
                </Typography>
              )}
              {c.onClick && (
                <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                  View full profile
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>Repair progress</Typography>
          <Chip size="small" color="primary" label={`${doneCount} of 7 steps done`} />
        </Box>
        <Box sx={{ height: 6, bgcolor: 'grey.200', borderRadius: 1, mb: 1.5, overflow: 'hidden' }}>
          <Box sx={{ height: '100%', width: `${(doneCount / 7) * 100}%`, bgcolor: doneCount === 7 ? 'success.main' : 'primary.main' }} />
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(7, 1fr)' },
            gap: 1
          }}
        >
          {STEP_META.map((s) => {
            const st = stepState(workflow, s.n);
            const selected = expandedStep === s.n;
            const Icon = s.Icon;
            return (
              <Button
                key={s.n}
                onClick={() => onSelectStep(s.n)}
                variant={selected ? 'contained' : 'outlined'}
                color={st.completed ? 'success' : 'primary'}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textTransform: 'none',
                  py: 1.25,
                  px: 0.75,
                  minHeight: 108,
                  borderWidth: selected ? 2 : 1,
                  bgcolor: selected && st.completed ? 'success.main' : undefined,
                  opacity: st.completed || st.current || selected ? 1 : 0.7
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    mb: 0.5,
                    bgcolor: selected ? 'rgba(255,255,255,0.2)' : st.completed ? 'success.main' : st.current ? 'primary.main' : 'grey.300',
                    color: st.completed || st.current || selected ? '#fff' : 'text.secondary'
                  }}
                >
                  {st.completed ? <CheckIcon fontSize="small" /> : <Icon fontSize="small" />}
                </Avatar>
                <Typography variant="caption" fontWeight={700} lineHeight={1.2}>
                  {s.n}. {s.name}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85, lineHeight: 1.2, mt: 0.25 }}>
                  {st.completed ? 'Done' : st.current ? 'In progress' : s.hint}
                </Typography>
              </Button>
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Click a step to see what was done. Completed steps stay marked automatically.
        </Typography>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: { xs: 1.5, md: 2.5 }, minHeight: 360 }}>
            {(() => {
              const meta = STEP_META.find((s) => s.n === expandedStep) || STEP_META[0];
              const st = stepState(workflow, expandedStep);
              return (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      color={st.completed ? 'success' : st.current ? 'primary' : 'default'}
                      label={st.completed ? 'Completed' : st.current ? 'Current step' : 'Upcoming'}
                    />
                    <Typography variant="h6" fontWeight={700}>
                      Step {meta.n} — {st.name || meta.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {stepDoneLabel(data, expandedStep)}
                    {st.at ? ` · ${fmtWhen(st.at)}` : ''}
                    {st.notes ? ` · ${st.notes}` : ''}
                  </Typography>
                  <Divider sx={{ mt: 1.5 }} />
                </Box>
              );
            })()}
            {children}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%', minHeight: 360 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Activity timeline
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Everything that happened on this job
            </Typography>
            {timeline.length === 0 ? (
              <Typography color="text.secondary" variant="body2">No activity yet.</Typography>
            ) : (
              <Box sx={{ position: 'relative', pl: 2.5 }}>
                <Box sx={{ position: 'absolute', left: 7, top: 6, bottom: 6, width: 2, bgcolor: 'divider' }} />
                {timeline.map((t) => (
                  <Box key={t.id || `${t.created_at}-${t.title}`} sx={{ position: 'relative', mb: 2 }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        left: -22,
                        top: 4,
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: t.event_type === 'workflow' ? 'success.main' : 'primary.main',
                        border: '2px solid #fff',
                        boxShadow: 1
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {fmtWhen(t.created_at)}
                      {t.created_by ? ` · ${t.created_by}` : ''}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>{t.title}</Typography>
                    {t.description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RepairJobDashboard;
